import React, { useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Icon } from 'components/Icon';
import { Modal, ModalContent, ModalHeader } from 'components/Modal';
import { H2, H3, Label, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { IPoolData, TipCalculatorEligibilityMethodLabel, TipCalculatorReceivedDistributionType } from 'types';
import { buildPoolReport, centsToDollarsDisplay, Duration, toTitleCase } from 'utils';

interface IPoolReportEmployeeProps {
  onClose: () => void;
  pool: IPoolData;
  jobTitle: string;
  businessDay: string;
}

const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(theme.space.lg)};
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledModalContent = styled(ModalContent)`
  border-radius: ${toRem(8)};
  margin-top: ${toRem(theme.space.sm)};
`;

const ContentHeaderWrapper = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-around;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};
  flex-direction: column;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
  }
`;

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.xs)};
  justify-items: center;
  width: 100%;

  > * {
    margin-bottom: 0;
  }
`;

const HeaderItem = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  margin-bottom: ${toRem(theme.space.md)};

  label {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
    color: ${theme.color.neutral[500]};
    margin-bottom: ${toRem(theme.space.sm)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;

    label {
      font-size: ${toRem(theme.font.size.xs)};
      line-height: ${lineHeight(theme.font.size.xs)};
      margin-bottom: ${toRem(theme.space.xs)};
    }
  }
`;

const PoolReportRowWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  padding: ${toRem(theme.space.lg)};
`;

const Employee = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  flex-direction: column;
  align-items: start;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: grid;
    grid-template-columns: repeat(1, minmax(auto, 100%)) repeat(3, minmax(${toRem(80)}, 1fr));
    grid-gap: ${toRem(theme.space.xs)};
  }
`;

const EmployeeInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(theme.space.md)};

  p.label {
    color: ${theme.color.neutral[500]};
  }

  p:nth-child(2) {
    display: flex;
    align-items: center;
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
  }

  p > span {
    padding: ${toRem(theme.space['2xs'])} ${toRem(theme.space.xs)};
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
    margin-left: ${toRem(theme.space.xs)};
    color: ${theme.color.neutral[900]};
  }

  p > svg {
    margin-right: ${toRem(theme.space.xs)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;
  }
`;

const EmployeeTotal = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(theme.space.md)};

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
    margin-bottom: ${toRem(theme.space.xs)};
  }

  :last-child {
    margin-bottom: 0;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    margin-bottom: 0;
  }
`;

const CalculatorReportNavWrapper = styled.div`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: flex-start;
  align-items: center;
  flex-flow: row nowrap;
  gap: ${toRem(theme.space.xs)};

  @media (min-width: ${theme.breakpoint.small}px) {
    /* off-scale exception: 1px optical nudge (2026-09-16) */
    margin-bottom: ${toRem(1)};
  }
`;

const CalculatorReportNavItem = styled.div`
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.sm)};
  color: ${theme.color.neutral[600]};
  border-radius: ${theme.borderRadius.xsmall}px;
  font-weight: ${theme.font.weight.medium};

  &.active {
    background-color: ${theme.color.secondary[400]};
    color: #fff;
  }

  &:hover:not(.active) {
    cursor: pointer;
    background-color: ${theme.color.neutral[200]};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    width: 100%;
    text-align: center;
    padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)};
  }
`;

const PoolModalContentWrapper = styled.div`
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background: ${theme.color.neutral[50]};
  width: 100%;
  border-radius: ${toRem(0)} ${toRem(0)} ${toRem(8)} ${toRem(8)};
  align-items: center;
  padding: ${toRem(theme.space.lg)};
  flex-flow: column nowrap;

  @media (min-width: ${theme.breakpoint.small}px) {
    border-radius: ${toRem(8)};
  }
`;

const PoolModalContentNav = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};
  flex-direction: column;

  @media (min-width: ${theme.breakpoint.small}px) {
    flex-direction: row;
  }
`;

const StyledModalContentTitle = styled(Text)`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.semiBold};
  line-height: ${lineHeight(theme.font.size.md)};
`;

const PoolModalTabContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background: ${theme.color.white};
  border-radius: ${toRem(8)};

  > div:not(:last-child) {
    border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

const Rule = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  > div:last-child p {
    margin-top: ${toRem(theme.space.sm)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: grid;
    grid-template-columns: repeat(1, minmax(auto, 100%)) repeat(1, minmax(${toRem(55)}, 1fr));
    grid-gap: ${toRem(theme.space.xs)};
  }
`;

const RuleInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;

  p.label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }
`;

const HeaderDateSegmentWrapper = styled.div`
  display: flex;

  p:first-child {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
  }

  p:last-child {
    padding: ${toRem(theme.space.xs)} ${toRem(theme.space.sm)};
    background: ${theme.color.neutral[200]};
    border-radius: ${toRem(4)};
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.medium};
    margin-left: ${toRem(theme.space.xs)};
  }
`;

const DisclaimerContainer = styled.div`
  width: 100%;
  background-color: ${theme.color.warning[25]};
  padding: ${toRem(theme.space.sm)};
  border-radius: ${toRem(8)} ${toRem(8)} ${toRem(0)} ${toRem(0)};
  display: flex;
  flex-flow: column nowrap;
  border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-left: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-right: ${toRem(1)} solid ${theme.color.neutral[200]};

  p,
  a {
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.medium};
    text-align: center;
    text-decoration: none;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    display: inherit;
    border-radius: ${toRem(8)};
    border: none;
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const DisclaimerHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
    flex-direction: column;
  }
`;

enum View {
  EMPLOYEES = 'employees',
  RULES = 'rules',
}

const PoolModal = ({ onClose, pool, jobTitle, businessDay }: IPoolReportEmployeeProps) => {
  const [view, setView] = useState<View>(View.EMPLOYEES);
  const { employeeRows, receivedFromRows, totalMinWorked, totalReceivedTips, totalReceivedAutograts, totalReceived } = buildPoolReport(pool.result, jobTitle);
  return (
    <Modal data-cy="pool-report-modal" isActive onClose={onClose} maxWidth={640}>
      <StyledModalHeader>
        <HeaderContent>
          <H2 data-cy="modal-job-header" fontWeight="semibold">
            {toTitleCase(jobTitle)} Pool
          </H2>
          <HeaderDateSegmentWrapper>
            <Text data-cy="modal-date-header" fontWeight="medium">
              {dayjs(businessDay).format('MMMM Do, YYYY')}
            </Text>
            <Text data-cy="modal-segment-header">{pool.daySegment}</Text>
          </HeaderDateSegmentWrapper>
        </HeaderContent>
      </StyledModalHeader>
      <StyledModalContent>
        <DisclaimerHeaderWrapper>
          <DisclaimerContainer>
            <Text>These amounts do not include retained tips.</Text>
          </DisclaimerContainer>
          <ContentHeaderWrapper>
            <HeaderItem>
              <Label>Time Worked</Label>
              <H3 data-cy="modal-content-total-time">{new Duration(totalMinWorked).toString()}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Received Tips</Label>
              <H3 data-cy="modal-content-total-tips-received">{centsToDollarsDisplay(totalReceivedTips)}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Received Autograts</Label>
              <H3 data-cy="modal-content-total-autograts-received">{centsToDollarsDisplay(totalReceivedAutograts)}</H3>
            </HeaderItem>
            <HeaderItem>
              <Label>Total</Label>
              <H3 data-cy="modal-content-total">{centsToDollarsDisplay(totalReceived)}</H3>
            </HeaderItem>
          </ContentHeaderWrapper>
        </DisclaimerHeaderWrapper>

        <PoolModalContentWrapper>
          <PoolModalContentNav>
            <StyledModalContentTitle>Pool Details</StyledModalContentTitle>

            <CalculatorReportNavWrapper data-cy="pool-report-modal-nav">
              <CalculatorReportNavItem onClick={() => setView(View.EMPLOYEES)} {...(view === View.EMPLOYEES && { className: 'active' })} data-cy="pool-report-modal-nav-item">
                Employees
              </CalculatorReportNavItem>
              <CalculatorReportNavItem onClick={() => setView(View.RULES)} {...(view === View.RULES && { className: 'active' })} data-cy="pool-report-modal-nav-item">
                Rules
              </CalculatorReportNavItem>
            </CalculatorReportNavWrapper>
          </PoolModalContentNav>
          <PoolModalTabContent data-cy="pool-report-modal-content">
            {view === View.EMPLOYEES && (
              <>
                {employeeRows.map(({ employee, tips, autograts, total, percentWorked }, index) => (
                  <PoolReportRowWrapper key={index}>
                    <Employee data-cy="pool-report-employees-row">
                      <EmployeeInfo>
                        <Text data-cy="employee-name" fontWeight="semibold" size="small">
                          {employee.name}
                        </Text>
                        <Text data-cy="employee-time" size="small" className="label">
                          <Icon icon="Time" /> {new Duration(employee.totals.totalMinWorked).toString()} <span>{percentWorked}</span>
                        </Text>
                      </EmployeeInfo>
                      <EmployeeTotal>
                        <Label>Tips</Label>
                        <Text data-cy="employee-tip" size="small">
                          {centsToDollarsDisplay(tips)}
                        </Text>
                      </EmployeeTotal>
                      <EmployeeTotal>
                        <Label>Autograts</Label>
                        <Text data-cy="employee-autograts" size="small">
                          {centsToDollarsDisplay(autograts)}
                        </Text>
                      </EmployeeTotal>
                      <EmployeeTotal>
                        <Label>Total</Label>
                        <Text data-cy="employee-total" fontWeight="semibold" size="small">
                          {centsToDollarsDisplay(total)}
                        </Text>
                      </EmployeeTotal>
                    </Employee>
                  </PoolReportRowWrapper>
                ))}
              </>
            )}

            {view === View.RULES && (
              <>
                {receivedFromRows.map((rf, index) => (
                  <PoolReportRowWrapper key={index}>
                    {rf.distribution === TipCalculatorReceivedDistributionType.SHARE && (
                      <Rule data-cy="pool-report-rule-row">
                        <RuleInfo>
                          <Text data-cy="rule-info" fontWeight="semibold" size="small">
                            {toTitleCase(rf.name)} shared {rf.sharePercent} of {rf.category ? toTitleCase(rf.category) : rf.sources}
                          </Text>
                          <Text data-cy="rule-share-mode" size="small" className="label">
                            per {TipCalculatorEligibilityMethodLabel[rf.eligibilityMethod]} split by {rf.split.toLowerCase().replace('_', ' ')}
                          </Text>
                        </RuleInfo>
                        <div>
                          <Text data-cy="rule-total" fontWeight="semibold" size="small">
                            {centsToDollarsDisplay(rf.total)}
                          </Text>
                        </div>
                      </Rule>
                    )}
                    {rf.distribution === TipCalculatorReceivedDistributionType.REMAINDER && (
                      <Rule data-cy="pool-report-rule-row">
                        <RuleInfo>
                          <Text data-cy="rule-info" fontWeight="semibold" size="small">
                            {toTitleCase(rf.name)} earning {rf.conditions && rf.conditions[0]?.hours ? `${rf.conditions[0].hours} hour${rf.conditions[0].hours !== 1 ? 's' : ''} of ` : ''} {rf.sources}
                          </Text>
                          <Text data-cy="rule-share-mode" size="small" className="label">
                            split by {rf.split.toLowerCase().replace('_', ' ')}
                          </Text>
                        </RuleInfo>
                        <div>
                          <Text data-cy="rule-total" fontWeight="semibold" size="small">
                            {centsToDollarsDisplay(rf.total)}
                          </Text>
                        </div>
                      </Rule>
                    )}
                  </PoolReportRowWrapper>
                ))}
              </>
            )}
          </PoolModalTabContent>
        </PoolModalContentWrapper>
      </StyledModalContent>
    </Modal>
  );
};

export default PoolModal;
